import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  ServerIcon,
  CubeIcon,
  CircleStackIcon,
  PhotoIcon,
  PlayIcon,
  StopIcon,
  TrashIcon,
  ArrowPathIcon,
  CloudArrowDownIcon,
  RocketLaunchIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  EyeIcon,
  CogIcon,
  DocumentTextIcon,
  PlusIcon,
  MinusIcon,
  ClockIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { dockerAPI } from '../services/api';
import SortableTable from '../components/SortableTable';

const Docker = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showConfirmDialog, setShowConfirmDialog] = useState(null);
  const [selectedStack, setSelectedStack] = useState('');
  const [showLogs, setShowLogs] = useState(null);
  const [showScaleDialog, setShowScaleDialog] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Handle stack selection from URL params (for navigation from stacks)
  useEffect(() => {
    const stackParam = searchParams.get('stack');
    if (stackParam && activeTab === 'services') {
      setSelectedStack(stackParam);
    }
  }, [searchParams, activeTab]);

  // Queries
  const { data: containersData, isLoading: loadingContainers } = useQuery(
    'docker-containers',
    dockerAPI.getContainers,
    { refetchInterval: 10000 }
  );

  const { data: volumesData, isLoading: loadingVolumes } = useQuery(
    'docker-volumes',
    dockerAPI.getVolumes,
    { enabled: activeTab === 'volumes', refetchInterval: 30000 }
  );

  const { data: imagesData, isLoading: loadingImages } = useQuery(
    'docker-images',
    dockerAPI.getImages,
    { enabled: activeTab === 'images', refetchInterval: 30000 }
  );

  const { data: stacksData, isLoading: loadingStacks } = useQuery(
    'docker-stacks',
    dockerAPI.getStacks,
    { enabled: ['stacks', 'services', 'overview'].includes(activeTab), refetchInterval: 15000 }
  );

  const { data: servicesData, isLoading: loadingServices } = useQuery(
    ['docker-services', selectedStack],
    () => dockerAPI.getServices(selectedStack || undefined),
    { enabled: activeTab === 'services', refetchInterval: 10000 }
  );

  const { data: logsData, isLoading: loadingLogs } = useQuery(
    ['service-logs', showLogs],
    () => dockerAPI.getServiceLogs(showLogs),
    { 
      enabled: !!showLogs,
      refetchInterval: 5000
    }
  );

  // Service Mutations
  const serviceActionMutation = useMutation(
    ({ action, id, replicas }) => {
      switch (action) {
        case 'update':
          return dockerAPI.updateService(id);
        case 'scale':
          return dockerAPI.scaleService(id, replicas);
        case 'delete':
          return dockerAPI.deleteService(id);
        default:
          throw new Error('Ação inválida');
      }
    },
    {
      onSuccess: (response, variables) => {
        alert(response.data.message);
        queryClient.invalidateQueries('docker-services');
        setShowScaleDialog(null);
        setShowConfirmDialog(null);
      },
      onError: (error) => {
        alert('Erro: ' + (error.response?.data?.error || error.message));
      }
    }
  );

  // Mutations
  const containerActionMutation = useMutation(
    ({ action, id, force }) => {
      switch (action) {
        case 'start':
          return dockerAPI.startContainer(id);
        case 'stop':
          return dockerAPI.stopContainer(id);
        case 'restart':
          return dockerAPI.restartContainer(id);
        case 'delete':
          return dockerAPI.deleteContainer(id, force);
        default:
          throw new Error('Ação inválida');
      }
    },
    {
      onSuccess: (response, variables) => {
        alert(response.data.message);
        queryClient.invalidateQueries('docker-containers');
        setShowConfirmDialog(null);
      },
      onError: (error, variables) => {
        alert('Erro: ' + (error.response?.data?.error || error.message));
      }
    }
  );

  const volumeDeleteMutation = useMutation(dockerAPI.deleteVolume, {
    onSuccess: (response) => {
      alert(response.data.message);
      queryClient.invalidateQueries('docker-volumes');
      setShowConfirmDialog(null);
    },
    onError: (error) => {
      alert('Erro: ' + (error.response?.data?.error || error.message));
    }
  });

  const imageActionMutation = useMutation(
    ({ action, id, image, force }) => {
      switch (action) {
        case 'delete':
          return dockerAPI.deleteImage(id, force);
        case 'pull':
          return dockerAPI.pullImage(image);
        default:
          throw new Error('Ação inválida');
      }
    },
    {
      onSuccess: (response) => {
        alert(response.data.message);
        queryClient.invalidateQueries('docker-images');
        setShowConfirmDialog(null);
      },
      onError: (error) => {
        alert('Erro: ' + (error.response?.data?.error || error.message));
      }
    }
  );

  const stackRedeployMutation = useMutation(dockerAPI.redeployStack, {
    onSuccess: (response) => {
      alert(response.data.message);
      queryClient.invalidateQueries('docker-stacks');
    },
    onError: (error) => {
      alert('Erro: ' + (error.response?.data?.error || error.message));
    }
  });

  const containers = containersData?.data?.containers || [];
  const volumes = volumesData?.data?.volumes || [];
  const images = imagesData?.data?.images || [];
  const stacks = stacksData?.data?.stacks || [];
  const services = servicesData?.data?.services || [];
  const logs = logsData?.data?.logs || [];

  const tabs = [
    { key: 'overview', label: 'Visão Geral', icon: ServerIcon, count: null },
    { key: 'containers', label: 'Containers', icon: CubeIcon, count: containers.length },
    { key: 'services', label: 'Serviços', icon: CogIcon, count: services.length },
    { key: 'volumes', label: 'Volumes', icon: CircleStackIcon, count: volumes.length },
    { key: 'images', label: 'Images', icon: PhotoIcon, count: images.length },
    { key: 'stacks', label: 'Stacks', icon: ServerIcon, count: stacks.length },
  ];

  const getStatusColor = (status) => {
    if (status.toLowerCase().includes('up')) return 'text-green-600';
    if (status.toLowerCase().includes('exited')) return 'text-red-600';
    return 'text-yellow-600';
  };

  const getStatusIcon = (status) => {
    if (status.toLowerCase().includes('up')) return CheckCircleIcon;
    if (status.toLowerCase().includes('exited')) return ExclamationTriangleIcon;
    return ExclamationTriangleIcon;
  };

  // Service helpers
  const getServiceStatusColor = (service) => {
    if (service.currentReplicas === service.desiredReplicas && service.desiredReplicas > 0) {
      return 'text-green-600';
    }
    if (service.currentReplicas < service.desiredReplicas) {
      return 'text-yellow-600';
    }
    return 'text-red-600';
  };

  const getServiceStatusIcon = (service) => {
    if (service.currentReplicas === service.desiredReplicas && service.desiredReplicas > 0) {
      return CheckCircleIcon;
    }
    if (service.currentReplicas < service.desiredReplicas) {
      return ClockIcon;
    }
    return ExclamationTriangleIcon;
  };

  // Service action handlers
  const handleScale = (service, increment) => {
    const newReplicas = Math.max(0, service.desiredReplicas + increment);
    setShowScaleDialog({ service, newReplicas });
  };

  const executeScale = () => {
    const { service, newReplicas } = showScaleDialog;
    serviceActionMutation.mutate({ 
      action: 'scale', 
      id: service.name, 
      replicas: newReplicas 
    });
  };

  const handleServiceAction = (action, service) => {
    if (action === 'delete') {
      setShowConfirmDialog({
        action,
        service,
        title: 'Confirmar Exclusão do Serviço',
        message: `Tem certeza que deseja remover o serviço "${service.name}"?`
      });
      return;
    }

    if (action === 'logs') {
      setShowLogs(service.name);
      return;
    }

    serviceActionMutation.mutate({ action, id: service.name });
  };

  const handleAction = (action, item, force = false) => {
    if (['delete', 'stop'].includes(action)) {
      setShowConfirmDialog({
        action,
        item,
        force,
        title: action === 'delete' ? 'Confirmar Exclusão' : 'Confirmar Parada',
        message: `Tem certeza que deseja ${action === 'delete' ? 'remover' : 'parar'} "${item.name || item.id}"?`
      });
      return;
    }

    if (activeTab === 'containers') {
      containerActionMutation.mutate({ action, id: item.id || item.name, force });
    } else if (activeTab === 'images') {
      imageActionMutation.mutate({ action, id: item.id, image: `${item.repository}:${item.tag}`, force });
    } else if (activeTab === 'stacks') {
      stackRedeployMutation.mutate(item.name);
    }
  };

  const executeConfirmedAction = () => {
    const { action, item, force } = showConfirmDialog;
    
    if (activeTab === 'containers') {
      containerActionMutation.mutate({ action, id: item.id || item.name, force });
    } else if (activeTab === 'volumes') {
      volumeDeleteMutation.mutate(item.name);
    } else if (activeTab === 'images') {
      imageActionMutation.mutate({ action, id: item.id, force });
    }
  };

  const ContainerRow = ({ container }) => {
    const StatusIcon = getStatusIcon(container.status);
    
    return (
      <tr className="hover:bg-gray-50">
        <td className="px-4 py-3 text-sm">
          <div className="flex items-center space-x-2">
            <StatusIcon className={`h-4 w-4 ${getStatusColor(container.status)}`} />
            <span className="font-mono text-xs">{container.id}</span>
          </div>
        </td>
        <td className="px-4 py-3 text-sm font-medium text-gray-900">
          <div className="max-w-xs truncate" title={container.name}>
            {container.name}
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-gray-600">
          <div className="max-w-sm truncate" title={container.image}>
            {container.image}
          </div>
        </td>
        <td className="px-4 py-3 text-sm">
          <span className={`font-medium ${getStatusColor(container.status)}`}>
            {container.status}
          </span>
        </td>
        <td className="px-4 py-3 text-sm text-gray-600">
          {container.ports || 'N/A'}
        </td>
        <td className="px-4 py-3 text-right">
          <div className="flex justify-end space-x-2">
            <Link
              to={`/docker-logs?container=${container.id}`}
              className="btn-secondary text-xs py-1 px-2 inline-flex items-center"
              title="Ver Logs"
            >
              <DocumentTextIcon className="h-3 w-3" />
            </Link>
            {container.status.toLowerCase().includes('up') ? (
              <button
                onClick={() => handleAction('stop', container)}
                className="btn-secondary text-xs py-1 px-2"
                title="Parar"
              >
                <StopIcon className="h-3 w-3" />
              </button>
            ) : (
              <button
                onClick={() => handleAction('start', container)}
                className="btn-primary text-xs py-1 px-2"
                title="Iniciar"
              >
                <PlayIcon className="h-3 w-3" />
              </button>
            )}
            <button
              onClick={() => handleAction('restart', container)}
              className="btn-secondary text-xs py-1 px-2"
              title="Reiniciar"
            >
              <ArrowPathIcon className="h-3 w-3" />
            </button>
            <button
              onClick={() => handleAction('delete', container)}
              className="btn-danger text-xs py-1 px-2"
              title="Remover"
            >
              <TrashIcon className="h-3 w-3" />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const VolumeRow = ({ volume }) => (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 text-sm font-medium text-gray-900">
        {volume.name}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {volume.driver}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        <span className={`font-mono ${volume.size === 'Calculando...' ? 'text-yellow-600' : 'text-gray-900'}`}>
          {volume.size}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <button
          onClick={() => handleAction('delete', volume)}
          className="btn-danger text-xs py-1 px-2"
          title="Remover"
        >
          <TrashIcon className="h-3 w-3" />
        </button>
      </td>
    </tr>
  );

  const ImageRow = ({ image }) => {
    const [showUsage, setShowUsage] = useState(false);
    
    return (
      <>
        <tr className="hover:bg-gray-50">
          <td className="px-4 py-3 text-sm">
            <span className="font-mono text-xs">{image.id}</span>
          </td>
          <td className="px-4 py-3 text-sm font-medium text-gray-900">
            <div className="max-w-xs truncate" title={image.repository}>
              {image.repository}
            </div>
          </td>
          <td className="px-4 py-3 text-sm text-gray-600">
            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
              {image.tag || 'latest'}
            </span>
          </td>
          <td className="px-4 py-3 text-sm text-gray-600">
            {image.size}
          </td>
          <td className="px-4 py-3 text-sm">
            <div className="flex items-center space-x-2">
              {image.isOrphan ? (
                <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">
                  Órfã
                </span>
              ) : (
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                  {image.totalUses} uso{image.totalUses !== 1 ? 's' : ''}
                </span>
              )}
              {!image.isOrphan && (
                <button
                  onClick={() => setShowUsage(!showUsage)}
                  className="text-blue-600 hover:text-blue-800 text-xs"
                  title="Ver detalhes de uso"
                >
                  <EyeIcon className="h-3 w-3" />
                </button>
              )}
            </div>
          </td>
          <td className="px-4 py-3 text-sm text-gray-500">
            {image.created}
          </td>
          <td className="px-4 py-3 text-right">
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => handleAction('pull', image)}
                className="btn-secondary text-xs py-1 px-2"
                title="Pull Latest"
              >
                <CloudArrowDownIcon className="h-3 w-3" />
              </button>
              <button
                onClick={() => handleAction('delete', image)}
                className="btn-danger text-xs py-1 px-2"
                title="Remover"
              >
                <TrashIcon className="h-3 w-3" />
              </button>
            </div>
          </td>
        </tr>
        
        {/* Usage Details Row */}
        {showUsage && !image.isOrphan && (
          <tr className="bg-blue-50 border-l-4 border-l-blue-400">
            <td colSpan="7" className="px-4 py-3">
              <div className="text-sm">
                <h4 className="font-medium text-gray-900 mb-2">
                  Usado por {image.totalUses} item{image.totalUses !== 1 ? 's' : ''}:
                </h4>
                
                {image.usedBy.services.length > 0 && (
                  <div className="mb-3">
                    <h5 className="text-xs font-medium text-gray-700 mb-1">
                      Serviços ({image.usedBy.services.length}):
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {image.usedBy.services.map((service, idx) => (
                        <div 
                          key={idx}
                          className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs"
                        >
                          <span className="font-medium">{service.name}</span>
                          {service.stack !== 'standalone' && (
                            <span className="text-green-600 ml-1">({service.stack})</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {image.usedBy.containers.length > 0 && (
                  <div>
                    <h5 className="text-xs font-medium text-gray-700 mb-1">
                      Containers ({image.usedBy.containers.length}):
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {image.usedBy.containers.map((container, idx) => (
                        <div 
                          key={idx}
                          className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs"
                        >
                          {container.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </td>
          </tr>
        )}
      </>
    );
  };

  const StackRow = ({ stack }) => (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 text-sm font-medium text-gray-900">
        {stack.name}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
          {stack.services} serviços
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {stack.orchestrator}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => {
              setActiveTab('services');
              setSelectedStack(stack.name);
              setSearchParams({ stack: stack.name });
            }}
            className="btn-secondary text-xs py-1 px-2 flex items-center space-x-1"
            title="Ver Serviços"
          >
            <EyeIcon className="h-3 w-3" />
            <span>Serviços</span>
          </button>
          <button
            onClick={() => handleAction('redeploy', stack)}
            disabled={stackRedeployMutation.isLoading}
            className="btn-primary text-xs py-1 px-2 flex items-center space-x-1"
            title="Redeploy Stack"
          >
            <RocketLaunchIcon className="h-3 w-3" />
            <span>Redeploy</span>
          </button>
        </div>
      </td>
    </tr>
  );

  const ServiceRow = ({ service }) => {
    const StatusIcon = getServiceStatusIcon(service);
    
    return (
      <tr className="hover:bg-gray-50">
        <td className="px-4 py-3 text-sm">
          <div className="flex items-center space-x-2">
            <StatusIcon className={`h-4 w-4 ${getServiceStatusColor(service)}`} />
            <span className="font-mono text-xs">{service.id}</span>
          </div>
        </td>
        <td className="px-4 py-3 text-sm font-medium text-gray-900">
          <div className="max-w-sm truncate" title={service.name}>
            {service.name}
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-gray-600">
          <div className="max-w-sm truncate" title={service.image}>
            {service.image}
          </div>
        </td>
        <td className="px-4 py-3 text-sm">
          <div className="flex items-center space-x-2">
            <span className={`font-medium ${getServiceStatusColor(service)}`}>
              {service.replicas}
            </span>
            <div className="flex space-x-1">
              <button
                onClick={() => handleScale(service, -1)}
                disabled={service.desiredReplicas <= 0}
                className="btn-secondary text-xs p-1"
                title="Diminuir réplicas"
              >
                <MinusIcon className="h-3 w-3" />
              </button>
              <button
                onClick={() => handleScale(service, 1)}
                className="btn-secondary text-xs p-1"
                title="Aumentar réplicas"
              >
                <PlusIcon className="h-3 w-3" />
              </button>
            </div>
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-gray-600">
          {service.mode}
        </td>
        <td className="px-4 py-3 text-sm text-gray-600">
          {service.ports || 'N/A'}
        </td>
        <td className="px-4 py-3 text-right">
          <div className="flex justify-end space-x-2">
            <Link
              to={`/docker-logs?service=${service.id || service.name}`}
              className="btn-secondary text-xs py-1 px-2 inline-flex items-center"
              title="Ver Logs"
            >
              <DocumentTextIcon className="h-3 w-3" />
            </Link>
            <button
              onClick={() => handleServiceAction('update', service)}
              className="btn-secondary text-xs py-1 px-2"
              title="Update/Restart"
            >
              <ArrowPathIcon className="h-3 w-3" />
            </button>
            <button
              onClick={() => handleServiceAction('delete', service)}
              className="btn-danger text-xs py-1 px-2"
              title="Remover"
            >
              <TrashIcon className="h-3 w-3" />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Gerenciamento Docker</h1>
        <p className="mt-1 text-sm text-gray-500">
          Visualize e gerencie containers, volumes, imagens e stacks
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`
                  flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm
                  ${activeTab === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
                {tab.count !== null && (
                  <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      <div className="card">
        {activeTab === 'overview' && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-6">
              Visão Geral do Docker
            </h3>
            
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center">
                  <CubeIcon className="h-8 w-8 text-blue-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-blue-600">Containers</p>
                    <p className="text-2xl font-bold text-blue-900">{containers.length}</p>
                  </div>
                </div>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <CogIcon className="h-8 w-8 text-green-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-green-600">Serviços</p>
                    <p className="text-2xl font-bold text-green-900">{services.length || 0}</p>
                  </div>
                </div>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center">
                  <ServerIcon className="h-8 w-8 text-purple-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-purple-600">Stacks</p>
                    <p className="text-2xl font-bold text-purple-900">{stacks.length}</p>
                  </div>
                </div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center">
                  <PhotoIcon className="h-8 w-8 text-yellow-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-yellow-600">Imagens</p>
                    <p className="text-2xl font-bold text-yellow-900">{images.length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Docker Hierarchy Diagram */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Hierarquia Docker</h4>
              <div className="flex flex-col space-y-4">
                {/* Stacks Level */}
                <div className="flex items-center justify-center">
                  <div className="bg-purple-100 border-2 border-purple-300 rounded-lg p-4 text-center">
                    <ServerIcon className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                    <span className="text-sm font-semibold text-purple-800">Stacks</span>
                    <br />
                    <span className="text-xs text-purple-600">({stacks.length} composições de serviços)</span>
                  </div>
                </div>
                
                {/* Arrow Down */}
                <div className="flex justify-center">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
                
                {/* Services, Containers, Volumes, Images Level */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-green-100 border-2 border-green-300 rounded-lg p-3 text-center">
                    <CogIcon className="h-6 w-6 text-green-600 mx-auto mb-1" />
                    <span className="text-xs font-semibold text-green-800">Serviços</span>
                    <div className="text-lg font-bold text-green-900 mt-1">{services.length}</div>
                    <span className="text-xs text-green-600">ativos</span>
                  </div>
                  <div className="bg-blue-100 border-2 border-blue-300 rounded-lg p-3 text-center">
                    <CubeIcon className="h-6 w-6 text-blue-600 mx-auto mb-1" />
                    <span className="text-xs font-semibold text-blue-800">Containers</span>
                    <div className="text-lg font-bold text-blue-900 mt-1">{containers.length}</div>
                    <span className="text-xs text-blue-600">{containers.filter(c => c.status.toLowerCase().includes('up')).length} rodando</span>
                  </div>
                  <div className="bg-orange-100 border-2 border-orange-300 rounded-lg p-3 text-center">
                    <CircleStackIcon className="h-6 w-6 text-orange-600 mx-auto mb-1" />
                    <span className="text-xs font-semibold text-orange-800">Volumes</span>
                    <div className="text-lg font-bold text-orange-900 mt-1">{volumes.length}</div>
                    <span className="text-xs text-orange-600">armazenamento</span>
                  </div>
                  <div className="bg-yellow-100 border-2 border-yellow-300 rounded-lg p-3 text-center">
                    <PhotoIcon className="h-6 w-6 text-yellow-600 mx-auto mb-1" />
                    <span className="text-xs font-semibold text-yellow-800">Imagens</span>
                    <div className="text-lg font-bold text-yellow-900 mt-1">{images.length}</div>
                    <span className="text-xs text-yellow-600">disponíveis</span>
                  </div>
                </div>
                
                {/* Stack Details */}
                {stacks.length > 0 && (
                  <div className="mt-6">
                    <h5 className="text-sm font-semibold text-gray-800 mb-3">Stacks Ativas:</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {stacks.slice(0, 9).map((stack) => (
                        <div 
                          key={stack.name} 
                          className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-900 truncate">
                              {stack.name}
                            </span>
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                              {stack.services}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {stack.services} serviço{stack.services !== 1 ? 's' : ''}
                          </div>
                        </div>
                      ))}
                      {stacks.length > 9 && (
                        <div className="bg-gray-100 border border-gray-200 rounded-lg p-3 flex items-center justify-center">
                          <span className="text-xs text-gray-500">
                            +{stacks.length - 9} stacks...
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="text-center text-sm text-gray-600 mt-4">
                  <p>As <strong>Stacks</strong> orquestram e geram os demais recursos Docker</p>
                  <p className="text-xs mt-1">Imagens são compartilhadas entre stacks diferentes</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'services' && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Serviços Docker
            </h3>
            
            {/* Stack Filter */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-4">
                  <label className="text-sm font-medium text-gray-700">
                    Filtrar por Stack:
                  </label>
                  <select
                    value={selectedStack}
                    onChange={(e) => {
                      setSelectedStack(e.target.value);
                      if (e.target.value) {
                        setSearchParams({ stack: e.target.value });
                      } else {
                        setSearchParams({});
                      }
                    }}
                    className="input text-sm"
                  >
                    <option value="">Todas as stacks</option>
                    {stacks.map((stack) => (
                      <option key={stack.name} value={stack.name}>
                        {stack.name} ({stack.services} serviços)
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="text-sm text-gray-600">
                  {selectedStack ? (
                    <span className="flex items-center space-x-2">
                      <ServerIcon className="h-4 w-4" />
                      <span>Stack: <strong>{selectedStack}</strong></span>
                    </span>
                  ) : (
                    <span>{services.length} serviço(s) total</span>
                  )}
                </div>
              </div>
            </div>

            {/* Services Table */}
            <SortableTable
              columns={[
                { key: 'id', label: 'ID', sortable: true, filterable: true },
                { key: 'name', label: 'Nome', sortable: true, filterable: true },
                { key: 'image', label: 'Imagem', sortable: true, filterable: true },
                { key: 'replicas', label: 'Réplicas', sortable: true, filterable: false },
                { key: 'mode', label: 'Modo', sortable: true, filterable: true },
                { key: 'ports', label: 'Portas', sortable: true, filterable: true },
                { key: 'actions', label: 'Ações', sortable: false, filterable: false }
              ]}
              data={services}
              loading={loadingServices}
              renderRow={(service) => <ServiceRow key={service.id} service={service} />}
              emptyState={
                <div className="text-center py-8">
                  <CogIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    {selectedStack ? `Nenhum serviço na stack "${selectedStack}"` : 'Nenhum serviço encontrado'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {selectedStack ? 'Tente selecionar outra stack.' : 'Não há serviços rodando no momento.'}
                  </p>
                </div>
              }
            />
          </div>
        )}

        {activeTab === 'containers' && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Containers ({containers.length})
            </h3>
            <SortableTable
              columns={[
                { key: 'id', label: 'ID', sortable: true, filterable: true },
                { key: 'name', label: 'Nome', sortable: true, filterable: true },
                { key: 'image', label: 'Imagem', sortable: true, filterable: true },
                { key: 'status', label: 'Status', sortable: true, filterable: true },
                { key: 'ports', label: 'Portas', sortable: true, filterable: true },
                { key: 'actions', label: 'Ações', sortable: false, filterable: false }
              ]}
              data={containers}
              loading={loadingContainers}
              renderRow={(container) => <ContainerRow key={container.id} container={container} />}
              emptyState={
                <div className="text-center py-8">
                  <CubeIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum container encontrado</h3>
                  <p className="mt-1 text-sm text-gray-500">Não há containers rodando no momento.</p>
                </div>
              }
            />
          </div>
        )}

        {activeTab === 'volumes' && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Volumes ({volumes.length})
            </h3>
            <SortableTable
              columns={[
                { key: 'name', label: 'Nome', sortable: true, filterable: true },
                { key: 'driver', label: 'Driver', sortable: true, filterable: true },
                { key: 'size', label: 'Tamanho', sortable: true, filterable: false },
                { key: 'actions', label: 'Ações', sortable: false, filterable: false }
              ]}
              data={volumes}
              loading={loadingVolumes}
              renderRow={(volume) => <VolumeRow key={volume.name} volume={volume} />}
              emptyState={
                <div className="text-center py-8">
                  <CircleStackIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum volume encontrado</h3>
                  <p className="mt-1 text-sm text-gray-500">Não há volumes criados no momento.</p>
                </div>
              }
            />
          </div>
        )}

        {activeTab === 'images' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Imagens ({images.length})
              </h3>
              {imagesData?.data && (
                <div className="flex space-x-4 text-sm">
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                    {imagesData.data.usedCount} em uso
                  </span>
                  <span className="bg-red-100 text-red-800 px-2 py-1 rounded">
                    {imagesData.data.orphanCount} órfãs
                  </span>
                </div>
              )}
            </div>
            <SortableTable
              columns={[
                { key: 'id', label: 'ID', sortable: true, filterable: true },
                { key: 'repository', label: 'Repository', sortable: true, filterable: true },
                { key: 'tag', label: 'Tag', sortable: true, filterable: true },
                { key: 'size', label: 'Tamanho', sortable: true, filterable: false },
                { key: 'isOrphan', label: 'Status/Uso', sortable: true, filterable: false },
                { key: 'created', label: 'Criado', sortable: true, filterable: false },
                { key: 'actions', label: 'Ações', sortable: false, filterable: false }
              ]}
              data={images}
              loading={loadingImages}
              renderRow={(image) => <ImageRow key={image.id} image={image} />}
              emptyState={
                <div className="text-center py-8">
                  <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma imagem encontrada</h3>
                  <p className="mt-1 text-sm text-gray-500">Não há imagens Docker no momento.</p>
                </div>
              }
            />
          </div>
        )}

        {activeTab === 'stacks' && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Stacks ({stacks.length})
            </h3>
            <SortableTable
              columns={[
                { key: 'name', label: 'Nome', sortable: true, filterable: true },
                { key: 'services', label: 'Serviços', sortable: true, filterable: false },
                { key: 'orchestrator', label: 'Orquestrador', sortable: true, filterable: true },
                { key: 'actions', label: 'Ações', sortable: false, filterable: false }
              ]}
              data={stacks}
              loading={loadingStacks}
              renderRow={(stack) => <StackRow key={stack.name} stack={stack} />}
              emptyState={
                <div className="text-center py-8">
                  <ServerIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma stack encontrada</h3>
                  <p className="mt-1 text-sm text-gray-500">Não há stacks Docker Swarm no momento.</p>
                </div>
              }
            />
          </div>
        )}
      </div>

      {/* Logs Modal */}
      {showLogs && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Logs: {showLogs}
              </h3>
              <button
                onClick={() => setShowLogs(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-xs max-h-96 overflow-y-auto">
              {loadingLogs ? (
                <div className="text-center text-gray-400">
                  Carregando logs...
                </div>
              ) : logs.length > 0 ? (
                logs.map((line, index) => (
                  <div key={index} className="mb-1">
                    {line}
                  </div>
                ))
              ) : (
                <div className="text-gray-400">
                  Nenhum log encontrado
                </div>
              )}
            </div>
            
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowLogs(null)}
                className="btn-secondary"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scale Dialog */}
      {showScaleDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <CogIcon className="mx-auto h-12 w-12 text-blue-500" />
              <h3 className="text-lg font-medium text-gray-900 mt-2">
                Escalar Serviço
              </h3>
              <p className="text-sm text-gray-500 mt-2">
                Serviço: <strong>{showScaleDialog.service.name}</strong>
              </p>
              <p className="text-sm text-gray-500">
                Réplicas atuais: <strong>{showScaleDialog.service.replicas}</strong>
              </p>
              <p className="text-sm text-gray-700 mt-2">
                Novas réplicas: <strong>{showScaleDialog.newReplicas}</strong>
              </p>
              
              <div className="flex justify-center space-x-4 mt-6">
                <button
                  onClick={() => setShowScaleDialog(null)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  onClick={executeScale}
                  className="btn-primary"
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
              <h3 className="text-lg font-medium text-gray-900 mt-2">
                {showConfirmDialog.title}
              </h3>
              <p className="text-sm text-gray-500 mt-2">
                {showConfirmDialog.message}
              </p>
              {showConfirmDialog.action === 'delete' && (
                <p className="text-xs text-red-600 mt-1">
                  Esta ação não pode ser desfeita.
                </p>
              )}
              
              <div className="flex justify-center space-x-4 mt-6">
                <button
                  onClick={() => setShowConfirmDialog(null)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  onClick={executeConfirmedAction}
                  className="btn-danger"
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Docker;